"use client";

import * as React from "react";
import Image from "next/image";
import clsx from "clsx";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";
import Underline from "./ui/underline";
import TextLinkButton from "@/components/ui/TextLinkButton";
import CtaButton from "@/components/ui/CtaButton";
import type { ButtonProps } from "@relume_io/relume-ui";

type BackButtonProps = ButtonProps & { href?: string };

type ImgData =
    | {
    src: string;
    blurDataURL?: string;
}
    | null;

type ConflictingHtmlEvents =
// animation events (React) vs motion props
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    // drag & drop (React) vs Framer Motion drag handlers
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type ClientProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof MotionProps | ConflictingHtmlEvents
> & {
    breadcrumbs?: BreadcrumbItem[];
    heading: string;
    button: BackButtonProps;
    img: ImgData;
    sizesHero: string;
};

const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.8,
            ease: [0.215, 0.61, 0.355, 1],
            delay: i * 0.1,
        },
    }),
} as const;

const curtainReveal = {
    hidden: { clipPath: "inset(0% 0% 100% 0%)" },
    visible: {
        clipPath: "inset(0% 0% 0% 0%)",
        transition: {
            duration: 1.2,
            ease: [0.65, 0, 0.35, 1],
            delay: 0.4,
        },
    },
} as const;

export default function BlogHeroClient({
                                           breadcrumbs,
                                           heading,
                                           button,
                                           img,
                                           sizesHero,
                                           className,
                                           ...rest
                                       }: ClientProps) {
    return (
        <LazyMotion features={domAnimation} strict>
            <m.section
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className={clsx(
                    "px-[5%] py-16 md:py-24 lg:py-28 mt-16 md:mt-14",
                    className
                )}
                {...rest}
            >
                <div className="container">
                    <div className="rb-12 mb-12 flex flex-col items-start justify-start md:mb-10">
                        {breadcrumbs && breadcrumbs.length > 0 ? (
                            <m.div variants={slideUp} custom={0} className="mb-10 md:mb-14 min-w-0 w-full max-w-full overflow-hidden">
                                <div className="relative inline-block min-w-0 max-w-full">
                                    <Breadcrumbs items={breadcrumbs} truncateLastOnMobile />
                                    <Underline
                                        delay={0.6}
                                        className="pointer-events-none absolute -left-12 top-full mt-2 text-accent h-5 w-[170px] md:h-6 md:w-[230px]"
                                    />
                                </div>
                            </m.div>
                        ) : (
                            <m.div variants={slideUp} custom={0} className="mb-8 md:mb-10 lg:mb-12">
                                <TextLinkButton
                                    text={button.title ?? "Wróć"}
                                    href={button.href ?? "/blog"}
                                    arrowDirection="left"
                                />
                            </m.div>
                        )}

                        <div className="grid w-full grid-cols-1 items-end gap-6 md:grid-cols-[3fr_1fr]">
                            <m.h1
                                variants={slideUp}
                                custom={1}
                                className="min-w-0 text-3xl font-medium md:text-5xl leading-[1.1] will-change-transform md:pr-8"
                            >
                                {heading}
                            </m.h1>
                            <m.div variants={slideUp} custom={2} className="will-change-transform flex justify-end md:justify-end">
                                <CtaButton />
                            </m.div>
                        </div>
                    </div>

                    <m.div
                        variants={curtainReveal}
                        className="mx-auto mb-0 w-full overflow-hidden rounded-[2rem] bg-secondary"
                    >
                        <div className="relative aspect-[5/2] w-full">
                            {img ? (
                                <Image
                                    src={img.src}
                                    alt={heading || ""}
                                    fill
                                    sizes={sizesHero}
                                    priority
                                    fetchPriority="high"
                                    placeholder={img.blurDataURL ? "blur" : "empty"}
                                    blurDataURL={img.blurDataURL}
                                    className="object-cover"
                                />
                            ) : null}
                        </div>
                    </m.div>
                </div>
            </m.section>
        </LazyMotion>
    );
}